import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import angular, {IController, IModule, IPromise, IRootScopeService, IScope, ITimeoutService} from "angular";
import _ from "lodash";
import {Melding, MeldingTilBruker, Varighet} from "felles/tjenester/MeldingTilBruker";
import {TransitionService} from "@uirouter/angularjs";
import {InnlastingsindikatorKontroller} from "../../direktiver/innlastingsindikator";

const css = require('./visMeldingerTilBruker.scss');

/**
 *  Meldinger i nettleservinduet.
 *  Bruk {@link MeldingTilBruker} for å registrere melding som skal vises.
 */
export class VisMeldingerTilBrukerKontroller implements IController {

    static registrerKomponent(angular: IModule) {
        angular
            .component('wmVisMeldingerTilBruker', {
                bindings: {visKun:'@'},
                controller: VisMeldingerTilBrukerKontroller.forInjisering(),
                template: require('./visMeldingerTilBruker.html')
            });
    }

    static forInjisering() {
        return [
            regNavn.ng.$scope,
            regNavn.ng.$timeout,
            regNavn.ng.ui.$transitions,
            MeldingTilBruker.regNavn,
            regNavn.ng.material.$mdToast,
            VisMeldingerTilBrukerKontroller
        ]
    }

    constructor(private $scope: IScope,
                private $timeout: ITimeoutService,
                private $transitions: TransitionService,
                private meldingerTjeneste: MeldingTilBruker,
                private $mdToast: angular.material.IToastService) {
    }

    visKun: string; //satt via bindings - kommaseparert liste bestående av info, advarsel og feil

    feil: Melding[];
    advarsel: Melding[];
    info: Melding[];

    private meldingstimeout = 10*1000; //ms vi skal vente før vi skjuler meldinger av typen "timout"
    private timeouts: { melding: Melding; timeout: IPromise<any> }[] = [];
    private fjernCallback: Function;


    harMeldinger() {
        return this.meldingerTjeneste.antallMeldinger() > 0;
    }

    feilmeldingerLest() {
        this.meldingerTjeneste.tømFeilmeldinger();
    }

    advarselmeldingerLest() {
        this.meldingerTjeneste.tømAdvarselmeldinger();
    }

    infomeldingerLest() {
        this.meldingerTjeneste.tømInfomeldinger();
    }

    klikkPaaMelding(melding: Melding) {
        if (melding.vedKlikk !== null && melding.vedKlikk()) this.meldingerTjeneste.fjernMelding(melding);
    }

    meldingLest(melding: Melding) {
        this.meldingerTjeneste.fjernMelding(melding);
    }

    $onInit(): void {
        this.registrerHendelseslyttere();
        this.konsumerMeldinger();
    }

    $onDestroy(): void {
        this.fjernCallback();
        //sørg for å fjerne alle timeouts (gjør ikke noe om de allerede har timet ut)
        this.fjernAlleTimouts();
    }

    //private hjelpemetoder

    private registrerHendelseslyttere() {
        this.fjernCallback = this.meldingerTjeneste.registrerCallback(() => this.konsumerMeldinger());
        this.$transitions.onStart({}, trans => { this.skjuleMeldingerVedNavigasjon(); });
    }

    private konsumerMeldinger() {
        if (this.skalNivåVises('info')) this.konsumerInfomeldinger();
        if (this.skalNivåVises('advarsel')) this.konsumerAdvarselmeldinger();
        if (this.skalNivåVises('feil')) this.konsumerFeilmeldinger();
    }

    private skalNivåVises(nivå:'info'|'advarsel'|'feil'){
        return _.isEmpty(this.visKun) || _.includes(this.visKun, nivå);
    }

    private konsumerFeilmeldinger() {
        const meldinger = this.meldingerTjeneste.feilmeldinger();
        _(this.filtrerMeldinger(meldinger, true))
            .forEach(melding => this.visFeilmeldingViaMdToast(melding));

        this.feil = this.filtrerMeldinger(meldinger, false);
        this.registrerTimoutForSkjuling(this.feil);
    }

    private konsumerAdvarselmeldinger() {
        this.advarsel = this.meldingerTjeneste.advarselmeldinger();
        this.registrerTimoutForSkjuling(this.advarsel);
    }

    private konsumerInfomeldinger() {
        const meldinger = this.meldingerTjeneste.infomeldinger();
        _(this.filtrerMeldinger(meldinger, true))
            .forEach(melding => this.visInfomeldingViaMdToast(melding));

        this.info = this.filtrerMeldinger(meldinger, false);
        this.registrerTimoutForSkjuling(this.info);
    }

    private filtrerMeldinger(meldinger: Melding[], erMdToastMelding: boolean): Melding[] {
        return _(meldinger)
            .filter(melding => (melding.varighet === Varighet.timeout && melding.vedKlikk === null) == erMdToastMelding)
            .value();
    }

    private registrerTimoutForSkjuling(meldinger: Melding[]) {
        const varigheterMedTimeout = _([Varighet.timeout_eller_navigasjon, Varighet.timeout]);
        _(meldinger)
            .filter(melding => varigheterMedTimeout.includes(melding.varighet))
            .filter(melding => !_(this.timeouts).some(timeout => timeout.melding === melding))
            .forEach(melding => {
                this.timeouts.push({
                    melding: melding,
                    timeout: this.$timeout(() => this.meldingerTjeneste.fjernMelding(melding), this.meldingstimeout)
                });
            });
    };

    private fjernAlleTimouts() {
        _(this.timeouts).forEach(t => this.$timeout.cancel(t.timeout));
    }

    private skjuleMeldingerVedNavigasjon() {
        this.meldingerTjeneste.fjernMeldingerMedVarighet(Varighet.sideskift);
        this.meldingerTjeneste.fjernMeldingerMedVarighet(Varighet.timeout_eller_navigasjon);
        _.remove(this.timeouts, t => t.melding.varighet == Varighet.timeout_eller_navigasjon)
            .forEach(t => this.$timeout.cancel(t.timeout));
    }

    //meldinger som vises via mdToast

    private visInfomeldingViaMdToast(melding: Melding) {
        const options = this.forberedMdToastOptions(melding)
            .toastClass('md-toast-infomelding');
        this.$mdToast.show(options);
        this.meldingerTjeneste.fjernMelding(melding)
    }

    private visFeilmeldingViaMdToast(melding: Melding) {
        const options = this.forberedMdToastOptions(melding)
            .toastClass('md-toast-feilmelding');
        this.$mdToast.show(options);
        this.meldingerTjeneste.fjernMelding(melding)
    }

    private forberedMdToastOptions(melding: Melding) {
        let delay = 3000;
        switch (melding.varighet) {
            /*
            //mangler en måte å klikke bort meldinger som ikke autoskjules
            case Varighet.klikkBort:
                delay = 0;
            */
        }
        const options = this.$mdToast.simple()
            .textContent(melding.melding)
            .hideDelay(delay);
        /*
        //melding.vedKlikk håndteres foreløpig ikke..
        if(melding.vedKlikk)
            options.action("handling")
        */

        return options;
    }

}
